import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import { styled, useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import { FAQ_CHECK_HISTORY_URL, OWNER_CONTRIBUTIONS_URL } from '#client/constants/FAQs';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import Footer from '#client/pages/Dashboard/components/Footer';
import { GET_BANK_CUSTOMER_ADDRESS } from '#client/queries';

import StepItem from './StepItem';

import {
  DialogContentStyle,
  LinkButtonStyle,
  LinkStyles,
  StepNumberStyle,
  WrapperStyle
} from './styles';

const Wrapper = styled(Stack)(WrapperStyle);
const StepNumberStyled = styled(Stack)(StepNumberStyle);
const LinkStyled = styled(Link)(LinkStyles);
const DialogContentStyled = styled(DialogContent)(DialogContentStyle);
const LinkButtonStyled = styled(Button)(LinkButtonStyle);

const steps = [
  {
    title: 'Log in to the other bank or app',
    description: 'Transactions will happen there, not on your Banking dashboard.'
  },
  {
    title: 'Search for how to link a bank account',
    description: 'Words to look for: transfers, send money, add bank, external accounts'
  },
  {
    title: 'Add ZenBusiness Banking ',
    description: 'When connecting, provide your account info.'
  },
  {
    title: 'Follow all steps to be verified',
    description: 'Trial deposits may be used to verify your account.'
  }
];

export default function ManualAddInfoBankModal({ handleClose }) {
  const theme = useTheme();
  const { isWebView } = WebView;
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId, isEnabled, isEnrolled, isValidMfa } = useUnitAccountId();
  const { loading: loadingUnitAccount, unitAccount, error: errorUnitAccount } = useUnitAccount();
  const { createCommonErrorToast } = useToastContext();
  const [isShowAccountDetails, setIsShowAccountDetails] = useState(false);

  useModalDisplayTracker({
    elementName: 'link_account_from_other_app',
    elementText: 'link_account_from_other_app',
    appPageName: 'link_account_from_other_app'
  });

  const {
    error,
    errors,
    loading: loadingBankCustomerAddress,
    data
  } = useQuery(GET_BANK_CUSTOMER_ADDRESS, {
    variables: {
      businessEntityUuid
    },
    skip: !isEnabled || !isEnrolled || !isValidMfa || !unitAccountId,
    noThrow: true
  });

  useLogError(errors, error);
  // convert unitAccount?.address attributes from null or undefined to empty string
  const bankCustomerAddress = useMemo(() => {
    const { address1, address2, city, state, zip } = data?.bankCustomerAddress ?? {};
    return {
      address1: address1 ?? '',
      address2: address2 ?? '',
      city: city ?? '',
      state: state ?? '',
      zip: zip ?? ''
    };
  }, [data]);

  const { address1, address2, city, state, zip } = bankCustomerAddress;

  const copyAddress = useMemo(() => {
    if (data?.bankCustomerAddress) {
      return `${address1} ${address2 ? `${address2} ` : ''}${city}, ${state}, ${zip}`;
    }
    return '';
  }, [address1, address2, city, data?.bankCustomerAddress, zip, state]);

  const renderAddress = useMemo(() => {
    if (data?.bankCustomerAddress) {
      return [`${address1} ${address2 ? `${address2} ` : ''}`, `${city}, ${state}, ${zip}`];
    }
    return [];
  }, [address1, address2, city, data?.bankCustomerAddress, zip, state]);

  const isHideSkeleton =
    errorUnitAccount || (unitAccount && !loadingUnitAccount && !loadingBankCustomerAddress);
  const [showSkeleton] = useSkeleton(isHideSkeleton);

  const menuItems = useMemo(() => {
    const {
      accountNumber = '**********',
      routingNumber = '**********',
      name = ''
    } = unitAccount ?? {};

    const createStepItem = (key, menuKey, copyText, texts, title) => (
      <StepItem
        copyText={copyText}
        key={key}
        menuKey={menuKey}
        texts={texts}
        title={title}
      />
    );

    return [
      createStepItem(
        'account-number',
        'account-number',
        accountNumber,
        [accountNumber],
        'ACCOUNT NUMBER'
      ),
      createStepItem(
        'routing-number',
        'routing-number',
        routingNumber,
        [routingNumber],
        'ROUTING NUMBER'
      ),
      <Stack key="wire-transfer">
        <Text
          color="primary"
          size="xs"
        >
          For Wire Transfers
        </Text>
      </Stack>,
      createStepItem('account-name', 'account-name', name, [name], 'ACCOUNT NAME'),
      createStepItem('bank-name', 'bank-name', 'Thread Bank', ['Thread Bank'], 'BANK NAME'),
      createStepItem('bank-address', 'bank-address', copyAddress, renderAddress, 'ADDRESS')
    ];
  }, [copyAddress, renderAddress, unitAccount]);
  const handleShowAccountDetails = () => {
    setIsShowAccountDetails(!isShowAccountDetails);
  };

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <FormStepModal
      handleClose={handleClose}
      isHideBackButton
      title=""
      titleBackground={theme.palette.background.grey.main}
    >
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper
              alignItems="center"
              spacing="48px"
            >
              <Stack
                alignItems="center"
                spacing="24px"
              >
                <Heading
                  align="center"
                  family="secondary"
                  size="5xl"
                  weight="bold"
                >
                  Link from another website or app
                </Heading>
                <Text
                  color="secondary"
                  size="lg"
                >
                  Move money from another bank or financial app, like Venmo, PayPal, or Stripe.
                </Text>
              </Stack>
              <Stack
                alignItems="center"
                spacing="64px"
              >
                <Stack spacing="32px">
                  <Stack
                    direction="row"
                    spacing="20px"
                  >
                    <StepNumberStyled
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text
                        color="white"
                        size="lg"
                        weight="medium"
                      >
                        1
                      </Text>
                    </StepNumberStyled>
                    <Stack spacing="8px">
                      <Text
                        color="primary"
                        size="md"
                        weight="medium"
                      >
                        Copy ZenBusiness Banking account and routing numbers
                      </Text>
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing="4px"
                      >
                        {showSkeleton ? (
                          <Skeleton
                            aria-label="loading account details"
                            height="16px"
                            width="100px"
                          />
                        ) : (
                          <LinkButtonStyled
                            color="info"
                            icon={
                              isShowAccountDetails ? (
                                <SharedAsset
                                  alt=""
                                  src="/icon/micro/eye-simple-hide.svg"
                                  width={24}
                                />
                              ) : (
                                <SharedAsset
                                  alt=""
                                  src="/icon/micro/eye-simple-show.svg"
                                  width={24}
                                />
                              )
                            }
                            onClick={handleShowAccountDetails}
                            variant="text"
                          >
                            <Text
                              color="green"
                              size="md"
                              weight="medium"
                            >
                              {isShowAccountDetails
                                ? 'Hide account details'
                                : 'Show account details'}
                            </Text>
                          </LinkButtonStyled>
                        )}
                      </Stack>
                      {isShowAccountDetails && (
                        <Stack
                          aria-label="menu"
                          minHeight={40}
                          spacing="8px"
                        >
                          {menuItems}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                  {steps.map(({ title, description }, index) => (
                    <Stack
                      alignItems="center"
                      direction="row"
                      key={title}
                      spacing="20px"
                    >
                      <StepNumberStyled
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          color="white"
                          size="lg"
                          weight="medium"
                        >
                          {index + 2}
                        </Text>
                      </StepNumberStyled>
                      <Stack>
                        <Text
                          color="primary"
                          size="md"
                          weight="medium"
                        >
                          {title}
                        </Text>
                        <Text
                          color="primary"
                          size="sm"
                        >
                          {description}
                        </Text>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
                <Stack
                  alignItems="center"
                  spacing="16px"
                  width="100%"
                >
                  <LoadingButton
                    color="primary"
                    fullWidth
                    onClick={handleClose}
                    size="large"
                    variant="contained"
                  >
                    Close
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    fullWidth
                    onClick={() => window.open(FAQ_CHECK_HISTORY_URL)}
                    size="large"
                    sx={{ borderColor: 'transparent' }}
                    variant="outlined"
                  >
                    I’m still stuck
                  </LoadingButton>
                </Stack>
              </Stack>
              {!isWebView && (
                <Text size="md">
                  <LinkStyled
                    color="info"
                    href={OWNER_CONTRIBUTIONS_URL}
                    rel="noreferrer"
                    target="_blank"
                  >
                    See the benefits
                  </LinkStyled>{' '}
                  of adding personal funds to a business account.
                </Text>
              )}
            </Wrapper>
          </FocusGrid>
          <Stack
            alignItems="center"
            justifyContent="center"
          >
            <Footer />
          </Stack>
        </ContainerGrid>
      </DialogContentStyled>
    </FormStepModal>
  );
}

ManualAddInfoBankModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};
